// =============================================================================
// Optim TURPE V2 — LE TITRE DU RÉSULTAT (décision D27 du 05/10/2026, maquettes
// « 2 » et « 2 bis » du dossier d'ouverture).
//
// Ce que le client doit vivre : UNE SEULE unité de montant sur toute la page.
// Vague wording du 14/10/2026 (Q15) : « +2 821 € HT par an » sur une période
// de 365 ou 366 jours, sinon « sur la période du … au … », suivi de « soit
// environ … par an » quand le moteur sert l'équivalent annuel ; jamais « sur
// 365 jours ». Quand le conseil
// prudent (couvrir les dépassements) coûte plus cher qu'aujourd'hui alors que l'autre
// scénario est rentable, le titre dit ce qu'il y a à gagner et à quelle
// condition, au lieu d'un « Surcoût » qui fait passer au compteur suivant. Le
// conseil prudent reste présenté au temps « Choisir ».
//
// Règles tenues ici :
// - aucun montant n'est calculé : l'équivalent annuel est celui du moteur
//   (`economie.equivalent_annuel`), les montants de période ceux de
//   `outputs.arbitrage` ; s'il manque, on ne l'affiche pas (jamais un 0) ;
// - « par an » ne suit jamais un montant de période ;
// - la règle « 2 bis » ne joue que si l'étude affichée est la jambe « sans
//   dépassements » de la paire, que son état est un surcoût et que l'état de
//   la jambe « avec » est un gain. Deux surcoûts ou deux gains : l'écran
//   reste celui de la maquette 2. Sans bloc `outputs.arbitrage` qui décrive
//   la paire, elle joue sur les montants servis par les deux études (D4).
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    ETATS_DE_GAIN,
    lireChiffre,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import type {
    ChiffreLu,
    PeriodeLue,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { lireArbitrageServi } from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"
import { formatMontantSigne } from "@modules/courtage/utils/optim-turpe-v2.montant"

/** Les classes de la saison haute : leur pointe est une pointe d'hiver. */
const CLASSES_D_HIVER = new Set(["HPH", "HCH", "P", "PTE"])

const nombreFr = formateurFr({ maximumFractionDigits: 0 })

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** « 2025-10-01 » → « 01/10/2025 », ou `null`. */
function dateFr(iso: string | null): string | null {
    const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null
}

// -----------------------------------------------------------------------------
// Maquette 2 : les montants de tête
// -----------------------------------------------------------------------------

export type MontantsDeTete = {
    /** L'équivalent annuel servi par le moteur, ou `null` (non calculé). */
    annuelEur: number | null
    /** Le montant sur la période analysée, ou `null` (non servi). */
    periodeEur: number | null
    /** Vrai quand les deux montants sont égaux : une seule ligne. */
    unSeul: boolean
}

/**
 * Les montants du premier niveau. Le montant de la période est le montant
 * affiché ; sur 365 jours, l'équivalent annuel lui est égal et ne s'écrit pas
 * (`unSeul`) ; sinon il s'écrit une fois, dessous, « soit … par an ».
 */
export function lireMontantsDeTete(chiffre: ChiffreLu): MontantsDeTete {
    const periodeEur = chiffre.netteEur
    const annuelEur =
        periodeEur === null ? null : fini(chiffre.equivalentAnnuel?.nette_eur)
    return {
        annuelEur,
        periodeEur,
        unSeul:
            annuelEur !== null &&
            periodeEur !== null &&
            Math.round(annuelEur) === Math.round(periodeEur),
    }
}

/** « sur la période analysée, du 01/10/2025 au 30/09/2026 (365 jours) ». */
export function libellePeriodeAnalysee(periode: PeriodeLue): string {
    const du = dateFr(periode.dateFrom)
    const au = dateFr(periode.dateTo)
    const dates = du && au ? `, du ${du} au ${au}` : ""
    const jours = periode.jours !== null ? ` (${periode.jours} jours)` : ""
    return `sur la période analysée${dates}${jours}`
}

// -----------------------------------------------------------------------------
// Maquettes 2 et 2 bis : la forme du titre
// -----------------------------------------------------------------------------

/** La ligne « Ce montant suppose que vous couvrez votre pointe… » (maquette 2). */
export type LectureDesDepassements = {
    /** La pointe couverte est-elle une pointe d'hiver (classe de saison haute) ? */
    hiver: boolean
    /** Heures de dépassement du scénario « avec », arrondies, ou `null`. */
    heures: number | null
    /** L'économie du scénario « avec », sur la période analysée. */
    montantEur: number
    /** Le nombre de jours de la période analysée, ou `null`. */
    jours: number | null
}

export type TitreDuResultat =
    | { forme: "courant"; lecture: LectureDesDepassements | null }
    | {
          forme: "economie_possible"
          /** L'économie du scénario « avec dépassements », positive. */
          possibleEur: number
          /** Ce que coûterait de couvrir les dépassements, en valeur positive. */
          surcoutEur: number
          /** Le conseil prudent, signé, avec sa décomposition si servie. */
          prudent: {
              netteEur: number
              fermeEur: number | null
              depassementsEur: number | null
          }
          jours: number | null
          /** Les dates de la période analysée, pour « sur la période du … au … ». */
          dates?: DatesDePeriode | null
      }

/**
 * La forme du titre du résultat pour l'étude affichée, lue sur la paire.
 *
 * `sans` et `avec` sont les deux études de la paire telles que la page les
 * tient ; le bloc `outputs.arbitrage` doit décrire exactement cette paire
 * (sinon : forme courante, sans ligne de lecture).
 */
export function lireTitreDuResultat(
    etude: OptimTurpeV2Etude | null | undefined,
    sans: OptimTurpeV2Etude | null | undefined,
    avec: OptimTurpeV2Etude | null | undefined
): TitreDuResultat {
    const courant: TitreDuResultat = { forme: "courant", lecture: null }
    if (!etude?._id || !sans || !avec) return courant
    if (etude._id !== sans._id || sans._id === avec._id) return courant
    const arbitrage = lireArbitrageServi(sans, avec)

    const chiffreSans = lireChiffre(sans)
    const chiffreAvec = lireChiffre(avec)
    const avecEstUnGain =
        chiffreAvec.etat !== null && ETATS_DE_GAIN.includes(chiffreAvec.etat)
    if (!avecEstUnGain) return courant

    // Une seule unité sur la page : les montants de la période analysée.
    const jours = chiffreSans.periode.jours

    // --- Maquette 2 bis : le conseil prudent coûte, l'autre scénario gagne ---
    // Revue témoin du 15/10/2026 (D4) : le titre suit la situation du client,
    // pas la présence du bloc servi. Sans bloc qui décrive CETTE paire, la
    // règle joue sur les montants servis par les deux études elles-mêmes
    // (`economie.nette_eur`), sans décomposition du conseil prudent.
    if (chiffreSans.etat === "surcout") {
        const netteSans = arbitrage
            ? arbitrage.montants.sans.nette
            : chiffreSans.netteEur
        const netteAvec = arbitrage
            ? arbitrage.montants.avec.nette
            : chiffreAvec.netteEur
        if (
            netteSans === null ||
            netteAvec === null ||
            netteSans >= 0 ||
            netteAvec <= 0
        ) {
            return courant
        }
        return {
            forme: "economie_possible",
            possibleEur: Math.round(netteAvec),
            surcoutEur: Math.round(-netteSans),
            prudent: {
                netteEur: Math.round(netteSans),
                fermeEur: arbitrage?.montants.sans.ferme ?? null,
                depassementsEur: arbitrage?.montants.sans.depassements ?? null,
            },
            jours,
            dates: chiffreSans.periode,
        }
    }

    // --- Maquette 2 : la ligne qui dit ce que deviendrait l'économie ---
    if (!arbitrage) return courant
    const periodeSans = arbitrage.montants.sans.nette
    const periodeAvec = arbitrage.montants.avec.nette
    if (periodeSans === null || periodeAvec === null) return courant
    if (periodeAvec <= 0 || periodeAvec <= periodeSans) return courant
    const heures =
        arbitrage.dureeDepassementH !== null
            ? Math.round(arbitrage.dureeDepassementH)
            : null
    return {
        forme: "courant",
        lecture: {
            hiver:
                arbitrage.plage !== null &&
                CLASSES_D_HIVER.has(arbitrage.plage),
            heures: heures !== null && heures > 0 ? heures : null,
            montantEur: Math.round(periodeAvec),
            jours,
        },
    }
}

// -----------------------------------------------------------------------------
// Les textes, mot pour mot ceux des maquettes votées
// -----------------------------------------------------------------------------

/** Les dates d'une période analysée, lues par `lirePeriode`. */
export type DatesDePeriode = {
    dateFrom: string | null
    dateTo: string | null
}

/**
 * L'unité d'un montant (vague wording du 14/10/2026, Q15) : « par an » sur
 * une période de 365 ou 366 jours ; sinon « sur la période du 18/01/2026 au
 * 25/09/2026 », ou « sur la période étudiée » quand les dates manquent.
 * Jamais « sur 365 jours ».
 */
export function surLaPeriode(
    jours: number | null,
    dates?: DatesDePeriode | null
): string {
    if (jours === 365 || jours === 366) return "par an"
    const du = dateFr(dates?.dateFrom ?? null)
    const au = dateFr(dates?.dateTo ?? null)
    return du && au
        ? `sur la période du ${du} au ${au}`
        : "sur la période étudiée"
}

/**
 * Le montant de tête et son unité (Q15) : « +2 500 € HT par an » ; sur une
 * période plus courte, « +2 300 € HT sur la période du … au … », suivi de
 * « soit environ +2 450 € HT par an » quand le moteur sert l'équivalent
 * annuel (dès 335 jours, `restitution.py`). Aucun montant n'est calculé ici.
 */
export type MontantDeTete = {
    montantEur: number
    unite: string
    /** « soit environ +2 450 € HT par an », ou `null`. */
    soitEnviron: string | null
}

export function montantDeTete(chiffre: ChiffreLu): MontantDeTete | null {
    const nette = chiffre.netteEur
    if (nette === null) return null
    const unite = surLaPeriode(chiffre.periode.jours, chiffre.periode)
    const annuel =
        unite === "par an" ? null : fini(chiffre.equivalentAnnuel?.nette_eur)
    return {
        montantEur: nette,
        unite,
        soitEnviron:
            annuel === null
                ? null
                : `soit environ ${formatMontantSigne(annuel, "HT")} par an`,
    }
}

/** Maquette 2 : ce que deviendrait l'économie en assumant les dépassements (W38). */
export function phraseLectureDesDepassements(
    lecture: LectureDesDepassements
): string {
    const enAssumant =
        lecture.heures !== null
            ? `En assumant environ ${nombreFr.format(lecture.heures)} ${
                  lecture.heures > 1 ? "heures" : "heure"
              } de dépassement`
            : "En assumant les dépassements mesurés"
    return (
        `Ce montant correspond au scénario « Couvrir les dépassements », sans dépassement. ` +
        `${enAssumant}, l'économie estimée serait de ` +
        `${formatMontantSigne(lecture.montantEur, "HT")} ${surLaPeriode(lecture.jours)}.`
    )
}

type EconomiePossible = Extract<TitreDuResultat, { forme: "economie_possible" }>

/**
 * Maquette 2 bis : le montant mis en vert dans le titre, « +223 € HT sur 365
 * jours » : une économie se lit signée, comme partout (revue du 08/10, R1).
 */
export function montantEconomiePossible(titre: EconomiePossible): string {
    return `${formatMontantSigne(titre.possibleEur, "HT")} ${surLaPeriode(titre.jours, titre.dates)}`
}

/**
 * Maquette 2 bis : la ligne du prix de la tranquillité. Q14 : un coût en plus
 * s'écrit signé « − » (en rouge à l'écran).
 */
export function phrasePrixDeLaCouverture(titre: EconomiePossible): string {
    return (
        `Couvrir les dépassements : ` +
        `${formatMontantSigne(-titre.surcoutEur, "HT")} ${surLaPeriode(titre.jours, titre.dates)} face au contrat actuel, ` +
        `à choisir pour ne plus payer de dépassement.`
    )
}

/** Maquette 2 bis : la décomposition du conseil prudent. */
export function phraseDecompositionDuConseilPrudent(
    titre: EconomiePossible
): string {
    const { fermeEur, depassementsEur, netteEur } = titre.prudent
    const total = `${formatMontantSigne(netteEur, "HT")} ${surLaPeriode(titre.jours, titre.dates)}`
    if (fermeEur === null || depassementsEur === null) {
        return `Couvrir les dépassements : ${total}.`
    }
    const depassements =
        depassementsEur >= 0 ? "dépassements évités" : "dépassements ajoutés"
    return (
        `Couvrir les dépassements : puissances et formule ${formatMontantSigne(fermeEur, "HT")}, ` +
        `${depassements} ${formatMontantSigne(depassementsEur, "HT")}, soit ${total}.`
    )
}
